import { questionnaireCatalog } from './catalog';
import { QUESTIONNAIRE_LANGUAGES, type QuestionnaireLanguage, type QuestionnaireQuestion } from './types';
export { questionnaireCatalog, QUESTIONNAIRE_LANGUAGES };
export type { QuestionnaireQuestion } from './types';
export { PERSONALIZATION_TOPICS, QUESTIONNAIRE_TOPICS, CORE_QUESTIONNAIRE_FIELDS, SUGGESTION_CONSTRAINT_FIELDS } from './topics';
export type { PersonalizationTopic } from './topics';

export const QUESTIONNAIRE_VERSION = 2;
export const ONBOARDING_QUESTION_COUNT = questionnaireCatalog.filter(q => q.tier === 1).length;
export const TOTAL_QUESTIONS = questionnaireCatalog.length;
export const TOTAL_WAVES = Math.max(...questionnaireCatalog.filter(q => q.tier === 2).map(q => Number(q.askAt.split('_')[1])));

/** Reviewed copy only. Stable question/option order, no database cache or AI generation. */
export function getQuestionnaireQuestions(language: string | null | undefined = 'en'): QuestionnaireQuestion[] {
  const base = language?.toLowerCase().replace('_', '-').split('-')[0];
  const locale = QUESTIONNAIRE_LANGUAGES.includes(base as QuestionnaireLanguage) ? base as QuestionnaireLanguage : 'en';
  return questionnaireCatalog.map(q => ({
    ...q,
    text: q.text[locale],
    options: q.options.map(option => ({ value: option.value, label: option.label[locale] })),
  }));
}

export function getQuestionnaireTranslations(questionId: string) {
  return QUESTIONNAIRE_LANGUAGES.filter(language => language !== 'en').flatMap(language => {
    const question = getQuestionnaireQuestions(language).find(q => q.id === questionId);
    return question ? [{ language, text: question.text, options: question.options.map(o => o.label) }] : [];
  });
}

export function isQuestionnaireAnswer(question: QuestionnaireQuestion, value: unknown): value is string | string[] {
  const allowed = new Set(question.options.map(o => o.value));
  if (question.multiSelect) {
    return Array.isArray(value) && value.length > 0 && value.length <= question.multiSelect &&
      new Set(value).size === value.length && value.every(v => typeof v === 'string' && allowed.has(v)) &&
      (value.length === 1 || !value.some(v => question.exclusiveOptions?.includes(v)));
  }
  return typeof value === 'string' && allowed.has(value);
}

/** Shared selection semantics for every questionnaire surface. */
export function toggleQuestionnaireOption(question: { multiSelect?: number; exclusiveOptions?: string[] }, selected: string[], value: string): string[] {
  if (selected.includes(value)) return selected.filter(v => v !== value);
  if (question.exclusiveOptions?.includes(value)) return [value];
  const compatible = selected.filter(v => !question.exclusiveOptions?.includes(v));
  return compatible.length < (question.multiSelect || 1) ? [...compatible, value] : compatible;
}

export type QuestionnaireStage = 'onboarding' | 'wave' | 'all';

/** One contract for the next short session, notifications and explicit history. */
export function getQuestionnaireSet(context: Record<string, unknown>, stage?: QuestionnaireStage, waveNumber?: number, language?: string | null) {
  const progress = getQuestionnaireProgress(context);
  const selectedStage = stage || (progress.onboardingCompleted ? 'wave' : 'onboarding');
  const wave = selectedStage === 'wave' ? (waveNumber ?? progress.currentWave + 1) : undefined;
  const questions = getQuestionnaireQuestions(language).filter(q => selectedStage === 'all' ||
    (selectedStage === 'onboarding' ? q.tier === 1 : q.askAt === `wave_${wave}`));
  const pendingQuestionIds = questions.filter(q => getQuestionnaireAnswer(context, q) === undefined &&
    (selectedStage === 'all' || !progress.skipped.has(q.id))).map(q => q.id);
  return { questions, stage: selectedStage,
    ...(wave !== undefined && wave <= TOTAL_WAVES ? { waveNumber: wave } : {}),
    totalQuestions: questions.length,
    answeredInSet: questions.filter(q => getQuestionnaireAnswer(context, q) !== undefined).length,
    pendingQuestionIds,
    // Compatibility for installed clients. This historically meant pending, not skipped.
    skippedInSet: pendingQuestionIds,
  };
}

export function getQuestionnaireAnswer(context: Record<string, unknown>, question: QuestionnaireQuestion): string | string[] | undefined {
  const entry = context[question.contextField];
  if (!entry || typeof entry !== 'object' || !('value' in entry)) return undefined;
  return isQuestionnaireAnswer(question, entry.value) ? entry.value : undefined;
}

/** Version-one wave metadata and answers never count toward the redesigned questions. */
export function getQuestionnaireProgress(context: Record<string, unknown>) {
  const questions = getQuestionnaireQuestions();
  const skipped = new Set(Array.isArray(context._skippedQuestionIds) ? context._skippedQuestionIds.filter((id): id is string => typeof id === 'string') : []);
  const answered = (q: QuestionnaireQuestion) => getQuestionnaireAnswer(context, q) !== undefined;
  const settled = (q: QuestionnaireQuestion) => answered(q) || skipped.has(q.id);
  const onboardingCompleted = questions.filter(q => q.tier === 1).every(settled);
  let currentWave = 0;
  if (onboardingCompleted) {
    for (let wave = 1; wave <= TOTAL_WAVES; wave++) {
      if (!questions.filter(q => q.askAt === `wave_${wave}`).every(settled)) break;
      currentWave = wave;
    }
  }
  return { onboardingCompleted, currentWave, answeredCount: questions.filter(answered).length, skipped };
}

/** Describe the respondent's answers, not diagnoses or facts about an absent partner. */
export function summarizeQuestionnaire(context: Record<string, unknown>): string | null {
  const entries = getQuestionnaireQuestions().flatMap(q => {
    const value = getQuestionnaireAnswer(context, q);
    if (value === undefined) return [];
    const selected = Array.isArray(value) ? value : [value];
    const labels = q.options.filter(o => selected.includes(o.value)).map(o => o.label);
    return [`${q.text} — ${labels.join('; ')}`];
  });
  return entries.length ? `The user reports the following about their relationship. These are their own views, not an assessment of their partner.\n${entries.join('\n')}` : null;
}
